import { useEffect, useId, useRef, useState } from 'react';
import { StatusDotPill } from '@/shared';
import { useCan } from '@/hooks';
import { useAuth } from '@/app/AuthContext.jsx';
import { formatCLP } from '@/lib';
import { CHECKOUT_STATE_LABEL } from './channelPayinCopy';
import { useChannelPayin } from './useChannelPayin';
import './ChannelPayinPanel.css';

const offerLabel = (q) => q.receptor?.name || 'Receptor sin nombre';

function CopyLinkButton({ url }) {
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  };
  return (
    <button type="button" className="plp-morebtn" onClick={copy}>
      {copied ? 'Copiado' : 'Copiar link'}
    </button>
  );
}

// Presentational: every state of the charge is a prop of `view`, so Storybook
// and the tests drive it without the API. `view` is what useChannelPayin returns.
export function ChannelPayinView({ view, onPick, onStart, onRefresh }) {
  const headingId = useId();
  const headingRef = useRef(null);
  const {
    loading,
    error,
    refreshing,
    linkedCase,
    liveTask,
    quotesLoading,
    quotesError,
    startedCount = 0,
    offers,
    selectedId,
    busy,
    charge,
  } = view;

  // After a start succeeds, focus moves to the section title so the result is
  // announced and a keyboard user lands on it.
  useEffect(() => {
    if (startedCount > 0) headingRef.current?.focus();
  }, [startedCount]);

  let body;
  if (loading) {
    body = <p className="pli-detail__note">Cargando cobro…</p>;
  } else if (error) {
    body = (
      <p className="pli-detail__note" role="alert">
        No se pudo cargar el estado del cobro. Usa Actualizar para reintentar.
      </p>
    );
  } else if (!linkedCase) {
    body = (
      <p className="pli-detail__note">
        Este pedido todavía no tiene una causa vinculada, así que no hay nada
        que cobrar.
      </p>
    );
  } else if (!liveTask) {
    body = (
      <p className="pli-detail__note">
        Este pedido no tiene una diligencia viva, así que no hay un cobro que
        mostrar.
      </p>
    );
  } else if (charge?.paid) {
    body = (
      <>
        <StatusDotPill status="ok" label="Pagado" />
        {charge.amount && (
          <p className="pli-detail__note">
            Oferta del receptor: {charge.amount} (más la comisión de servicio)
          </p>
        )}
      </>
    );
  } else if (charge) {
    body = (
      <>
        <StatusDotPill status="warn" label="Cobro abierto" />
        {charge.resumed && (
          <p className="pli-detail__note">Cobro ya abierto, se retomó.</p>
        )}
        {charge.state && (
          <p className="pli-detail__note">
            Sesión al crearla:{' '}
            {CHECKOUT_STATE_LABEL[charge.state] ?? charge.state}
          </p>
        )}
        {charge.amount && (
          <p className="pli-detail__note">
            Oferta del receptor: {charge.amount} (más la comisión de servicio)
          </p>
        )}
        {charge.url ? (
          <>
            <span className="pli-charge__link">{charge.url}</span>
            <div className="pli-charge__actions">
              <CopyLinkButton url={charge.url} />
              <a
                className="pli-kapso"
                href={charge.url}
                target="_blank"
                rel="noreferrer"
              >
                Abrir link de pago
              </a>
            </div>
          </>
        ) : (
          <p className="pli-detail__note">
            La sesión de pago todavía no tiene link. Actualiza en un momento.
          </p>
        )}
      </>
    );
  } else if (quotesError) {
    body = (
      <p className="pli-detail__note" role="alert">
        No se pudieron cargar las ofertas. Usa Actualizar para reintentar.
      </p>
    );
  } else if (quotesLoading) {
    body = <p className="pli-detail__note">Cargando ofertas…</p>;
  } else if (offers.length === 0) {
    body = (
      <p className="pli-detail__note">
        Todavía no hay ofertas para cobrar. Aparecen cuando un receptor ofrece.
      </p>
    );
  } else {
    body = (
      <>
        <p className="pli-detail__note">
          {offers.length === 1
            ? 'Hay una oferta. Confírmala para cobrar.'
            : 'Elige la oferta que se cobra.'}
        </p>
        <ul className="pli-charge__offers">
          {offers.map((q) => (
            <li key={q.id}>
              <label className="pli-charge__offer">
                <input
                  type="radio"
                  name={`${headingId}-offer`}
                  checked={selectedId === q.id}
                  onChange={() => onPick(q.id)}
                />
                <span className="pli-charge__offer-main">{offerLabel(q)}</span>
                <span className="pli-charge__price">
                  {formatCLP(Number(q.price)) ?? 'Sin precio'}
                </span>
              </label>
            </li>
          ))}
        </ul>
        <div className="pli-charge__actions">
          <button
            type="button"
            className="plp-rowbtn"
            disabled={busy || !selectedId}
            onClick={onStart}
          >
            {busy ? 'Iniciando…' : 'Iniciar cobro'}
          </button>
        </div>
      </>
    );
  }

  return (
    <section
      className="pli-charge"
      aria-labelledby={headingId}
      aria-busy={busy || loading ? 'true' : undefined}
    >
      <div className="pli-charge__head">
        <h3
          id={headingId}
          ref={headingRef}
          tabIndex={-1}
          className="plp-detail__h"
        >
          Cobro
        </h3>
        <button
          type="button"
          className="plp-morebtn"
          disabled={refreshing}
          onClick={onRefresh}
        >
          {refreshing ? 'Actualizando…' : 'Actualizar'}
        </button>
      </div>
      <div aria-live="polite">{body}</div>
    </section>
  );
}

// "Iniciar cobro" of an accepted pedido. Needs both keys: the intake decision
// and the platform's read of quotes. The API's 403 stays the source of truth.
export default function ChannelPayinPanel(props) {
  // Keyed by pedido: nothing of one pedido (result, picked offer) carries over
  // to the next one.
  return <ChannelPayinPanelFor key={props.intakeId} {...props} />;
}

function ChannelPayinPanelFor({ intakeId, sender }) {
  const canAccept = useCan('channel-intake:accept');
  const canReadQuotes = useCan('quote:platform-read');
  const organizationId = useAuth()?.organizationId ?? null;
  const allowed = canAccept && canReadQuotes;
  const view = useChannelPayin({
    intakeId,
    sender,
    organizationId,
    enabled: allowed,
  });
  if (!allowed) return null;
  return (
    <ChannelPayinView
      view={view}
      onPick={view.onPick}
      onStart={view.onStart}
      onRefresh={view.onRefresh}
    />
  );
}
